import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Error caught by ErrorBoundary:', error);
    console.error('Component stack:', errorInfo.componentStack);

    // List of non-fatal errors that should be ignored
    const nonFatalErrors = [
      'ResizeObserver loop',
      'ResizeObserver loop completed with undelivered notifications',
      'ResizeObserver loop limit exceeded',
      'Cannot update a component while rendering a different component',
      'Maximum update depth exceeded',
      'Load failed',
      'Network request failed',
      'Failed to fetch',
      'Cannot read properties of null (reading',
      'Cannot read properties of undefined (reading',
      'cancelAnimationFrame',
      'requestAnimationFrame',
      "Can't perform a React state update on an unmounted component",
      'IntersectionObserver',
    ];

    // Ignore non-fatal errors
    if (nonFatalErrors.some((msg) => error.message.includes(msg))) {
      console.warn('Non-fatal error ignored:', error.message);
      return;
    }

    // Set error state to show fallback UI
    this.setState({ hasError: true, error });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', margin: '20px', border: '1px solid red', borderRadius: '5px' }}>
          <h2>Application Error</h2>
          <p>
            <strong>Error Message:</strong> {this.state.error?.message || 'Unknown error'}
          </p>
          <details style={{ marginTop: '15px', marginBottom: '15px' }}>
            <summary>Technical Details</summary>
            <pre style={{ padding: '10px', background: '#f5f5f5', overflow: 'auto' }}>
              {this.state.error?.stack || 'No stack trace available'}
            </pre>
          </details>
          <p>The application encountered an error while rendering. Please try the following:</p>
          <ul>
            <li>Open browser console (F12) to see detailed error messages</li>
            <li>Clear your browser cache and reload</li>
            <li>Check your network connection</li>
          </ul>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '10px',
            }}
          >
            Reload Application
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
